import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';

interface QRCodeDisplayProps {
  url: string;
  title?: string;
}

const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({ url, title = "QRコード" }) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string>('');

  // 強制的に完全なURLを生成（動的表示用）
  const actualUrl = (() => {
    console.log('=== QRCodeDisplay URL修正（動的表示用） ===');
    console.log('受け取ったurl:', url);
    console.log('url長:', url.length);
    
    // もしurlが不完全な場合は強制的に完全な動的URLを生成
    if (url === 'http://wine.orange-ark.jp' || url.length < 50) {
      const fixedUrl = 'http://wine.orange-ark.jp/public-wine-list?user=orangeconcept278%40gmail.com';
      console.log('URLが不完全なので動的URLに修正:', fixedUrl);
      return fixedUrl;
    }
    
    // Firebase Storage URL形式を動的表示形式に変換
    if (url.includes('firebasestorage.app') && url.includes('static/')) {
      // Firebase Storage URLからユーザーIDを抽出
      const urlParts = url.split('/');
      const staticIndex = urlParts.findIndex(part => part === 'static');
      if (staticIndex !== -1 && urlParts[staticIndex + 1]) {
        const userId = urlParts[staticIndex + 1];
        const dynamicUrl = `http://wine.orange-ark.jp/public-wine-list?user=${userId}`;
        console.log('Firebase Storage URLを動的表示URLに変換:', dynamicUrl);
        return dynamicUrl;
      }
    }
    
    // 古い静的HTML形式を新しい動的表示形式に変換
    if (url.includes('/static-wine-list/') && url.includes('.html')) {
      const userId = url.split('/static-wine-list/')[1].replace('.html', '');
      const dynamicUrl = `http://wine.orange-ark.jp/public-wine-list?user=${userId}`;
      console.log('古い静的HTML URLを動的表示URLに変換:', dynamicUrl);
      return dynamicUrl;
    }
    
    // 古い動的URL形式を新しい動的表示形式に変換
    if (url.includes('/wine-list?user=')) {
      const userId = url.split('user=')[1];
      const dynamicUrl = `http://wine.orange-ark.jp/public-wine-list?user=${userId}`;
      console.log('古い動的URLを新しい動的表示URLに変換:', dynamicUrl);
      return dynamicUrl;
    }
    
    return url;
  })();

  useEffect(() => {
    generateQRCode();
  }, [actualUrl]);

  const generateQRCode = async () => {
    setIsGenerating(true);
    setError('');
    
    try {
      const qrCodeUrl = await QRCode.toDataURL(actualUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
      setQrCodeDataUrl(qrCodeUrl);
    } catch (err) {
      console.error('QRコード生成エラー:', err);
      setError('QRコードの生成に失敗しました');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    console.log('=== コピー機能デバッグ ===');
    console.log('コピー対象URL:', actualUrl);
    console.log('URL長:', actualUrl.length);
    console.log('navigator.clipboard存在:', !!navigator.clipboard);
    console.log('isSecureContext:', window.isSecureContext);
    
    try {
      // モダンブラウザのClipboard APIを試行
      if (navigator.clipboard && window.isSecureContext) {
        console.log('Clipboard APIを使用');
        await navigator.clipboard.writeText(actualUrl);
        console.log('Clipboard API成功');
        alert(`✅ URLをクリップボードにコピーしました！\n\n${actualUrl}`);
        return;
      }
      
      // フォールバック: 古いブラウザやHTTP環境用
      console.log('フォールバック方式を使用');
      const textArea = document.createElement('textarea');
      textArea.value = actualUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      textArea.style.opacity = '0';
      textArea.style.width = '1px';
      textArea.style.height = '1px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      console.log('textarea作成完了, value:', textArea.value);
      console.log('textarea選択状態:', textArea.selectionStart, textArea.selectionEnd);
      
      try {
        const successful = document.execCommand('copy');
        console.log('execCommand結果:', successful);
        if (successful) {
          alert(`✅ URLをクリップボードにコピーしました！\n\n${actualUrl}`);
        } else {
          throw new Error('execCommand failed');
        }
      } finally {
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('クリップボードコピーエラー:', err);
      // より詳細なエラーメッセージを表示
      const errorMessage = `❌ URLのコピーに失敗しました。\n\n手動でコピーしてください:\n\n${actualUrl}`;
      alert(errorMessage);
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">
          📱 {title}
        </h3>
        
        {isGenerating && (
          <div className="flex items-center justify-center mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">生成中...</span>
          </div>
        )}
        
        {error && (
          <div className="text-red-600 mb-4 p-2 bg-red-50 rounded">
            {error}
          </div>
        )}
        
        {qrCodeDataUrl && !isGenerating && (
          <div className="space-y-3">
            <div className="flex justify-center">
              <img 
                src={qrCodeDataUrl} 
                alt="QRコード" 
                className="border border-gray-300 rounded"
              />
            </div>
            
            <div className="text-sm text-gray-600">
              <p className="mb-2">スマホでQRコードを読み取ってアクセス</p>
              <div 
                className="bg-gray-50 p-2 rounded text-xs break-all cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={(e) => {
                  // URLを選択状態にする
                  const range = document.createRange();
                  const selection = window.getSelection();
                  range.selectNodeContents(e.currentTarget);
                  selection?.removeAllRanges();
                  selection?.addRange(range);
                }}
                title="クリックしてURLを選択"
              >
                {actualUrl}
              </div>
              <p className="text-xs text-gray-500 mt-1">↑ クリックしてURLを選択できます</p>
            </div>
            
            <div className="space-y-2">
              <button
                onClick={copyToClipboard}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm transition-colors"
              >
                📋 URLをコピー
              </button>
              
              <button
                onClick={() => {
                  // より確実な方法：promptでURLを表示
                  console.log('prompt表示前のURL:', actualUrl);
                  const userInput = prompt('URLをコピーしてください:', actualUrl);
                  if (userInput) {
                    alert('✅ URLをコピーしました！');
                  }
                }}
                className="w-full bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm transition-colors"
              >
                📝 URLを表示（確実な方法）
              </button>
              
              <button
                onClick={() => {
                  // デバッグ用：URLの詳細を表示
                  alert(`URL詳細情報:\n\n完全なURL: ${actualUrl}\nURL長: ${actualUrl.length}\n\n元のURL: ${url}\n元のURL長: ${url.length}\n\nwindow.location.origin: ${window.location.origin}\nwindow.location.href: ${window.location.href}`);
                }}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded text-sm transition-colors"
              >
                🔍 URL詳細を表示
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodeDisplay;
